import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faUserPlus, faTimes, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import patientService from '../../services/patientService';
import { toast } from 'react-toastify';
import { getRoleDisplayName } from '../../utils/roleUtils';
import AbnormalReadings24hModal from './AbnormalReadings24hModal';
import '../Common/ActionButtons/ActionButtons.css';
import './PatientReviewCell.css';

/** Survives cell remounts so "Mark Reviewed" UI doesn't snap back to Pending. */
const reviewedLockByKey = new Map();

const formatHandledAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const optionId = (opt, role) =>
  String(
    opt?.id ||
      opt?.user_id ||
      (role === 'caregiver' ? opt?.caregiver_id : opt?.provider_id) ||
      ''
  );

const optionMatchesCurrentUser = (opt, currentUserId) => {
  if (!currentUserId) return false;
  const ids = [opt?.id, opt?.user_id, opt?.provider_id, opt?.caregiver_id]
    .filter((v) => v != null && v !== '')
    .map((v) => String(v));
  return ids.includes(String(currentUserId));
};

const currentUserDisplayName = (user) => {
  if (!user) return '';
  const fromParts = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fromParts || user.name || user.full_name || user.username || '';
};

const currentUserRoleLabel = (user, fallbackRole) => {
  const fromName = String(user?.role_name || fallbackRole || '').trim();
  if (fromName) return fromName;
  return getRoleDisplayName(user?.role_id) || '';
};

const optionLabel = (opt, role) => {
  if (!opt) return '';
  const name =
    opt.name ||
    opt.full_name ||
    `${opt.first_name || ''} ${opt.last_name || ''}`.trim();
  if (name) return name;
  const id = optionId(opt, role);
  return role === 'provider' ? `Provider #${id}` : `Caregiver #${id}`;
};

const lookupReviewerFromOptions = (reviewerId, providerOptions, caregiverOptions) => {
  const id = String(reviewerId || '');
  if (!id) return { name: '', role: '' };
  const fromProviders = (providerOptions || []).find((opt) => optionMatchesCurrentUser(opt, id));
  if (fromProviders) {
    return {
      name: optionLabel(fromProviders, 'provider'),
      role: fromProviders.role_name || getRoleDisplayName(fromProviders.role_id) || 'Provider'
    };
  }
  const fromCaregivers = (caregiverOptions || []).find((opt) => optionMatchesCurrentUser(opt, id));
  if (fromCaregivers) {
    return {
      name: optionLabel(fromCaregivers, 'caregiver'),
      role: fromCaregivers.role_name || getRoleDisplayName(fromCaregivers.role_id) || 'Caregiver'
    };
  }
  return { name: '', role: '' };
};

const patientKeyParts = (patient) =>
  [
    patient?.patient_table_id,
    patient?.id,
    patient?.user_id,
    patient?.patient_id
  ]
    .filter((v) => v != null && v !== '')
    .map((v) => String(v));

const buildLockKey = (practiceId, patient) => {
  const parts = patientKeyParts(patient);
  return `${practiceId || ''}:${parts[0] || 'unknown'}`;
};

const findReviewedLock = (practiceId, patient) => {
  const parts = patientKeyParts(patient);
  for (const part of parts) {
    const key = `${practiceId || ''}:${part}`;
    if (reviewedLockByKey.has(key)) return reviewedLockByKey.get(key);
  }
  return null;
};

const setReviewedLock = (practiceId, patient, lock) => {
  const parts = patientKeyParts(patient);
  const keys = parts.length ? parts : ['unknown'];
  keys.forEach((part) => {
    reviewedLockByKey.set(`${practiceId || ''}:${part}`, lock);
  });
};

const isNewerAbnormalThan = (patient, handledAt) => {
  if (!handledAt) return false;
  const handledMs = new Date(handledAt).getTime();
  if (Number.isNaN(handledMs)) return false;
  // ONLY check abnormal timestamps! Do NOT check generic measurement_date / last_measurement_date
  // because those update on normal vitals too!
  const measure =
    patient?.latest_abnormal_uploaded_at ||
    patient?.latest_abnormal_at ||
    patient?.abnormal_measurement_date;
  if (!measure) return false;
  const measureMs = new Date(measure).getTime();
  if (Number.isNaN(measureMs)) return false;
  return measureMs > handledMs + 60000;
};

/**
 * Abnormal Readings:
 * - Not reviewed yet: Mark Reviewed  OR  Assign (invite someone else to review)
 * - After reviewed: green reviewed icon (who/when on hover)
 */
const PatientReviewCell = ({
  patient,
  practiceId,
  caregiverOptions = [],
  providerOptions = [],
  onStatusChange,
  isAbnormalContext = true,
  compact = false
}) => {
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = String(currentUser?.id || currentUser?.user_id || '');

  const pid =
    patient?.patient_table_id ||
    patient?.id ||
    patient?.user_id ||
    patient?.patient_id;
  const pIdResolved = practiceId || patient?.practice_id;
  const skipNextSyncRef = useRef(false);

  const [isReviewed, setIsReviewed] = useState(false);
  const [reviewedAt, setReviewedAt] = useState(null);
  const [reviewedByName, setReviewedByName] = useState('');
  const [reviewedByRole, setReviewedByRole] = useState('');
  const [reviewedById, setReviewedById] = useState('');
  const [isSavingReview, setIsSavingReview] = useState(false);

  const [panelMode, setPanelMode] = useState(null); // null | 'invite'
  const anchorRef = useRef(null);
  const [sheetPos, setSheetPos] = useState(null);
  const [pickRole, setPickRole] = useState(() => {
    const roleId = Number(currentUser?.role_id);
    return roleId === 5 || roleId === 7 ? 'caregiver' : 'provider';
  });
  const [selectedId, setSelectedId] = useState('');
  const [isSavingPick, setIsSavingPick] = useState(false);

  const [lastInviteRole, setLastInviteRole] = useState(null);
  const [lastInviteName, setLastInviteName] = useState('');
  const [isUnassigning, setIsUnassigning] = useState(false);

  const [isModal24hOpen, setIsModal24hOpen] = useState(false);
  const [selectedReadings24h, setSelectedReadings24h] = useState([]);

  const resolveReviewedFromPatient = (p) => {
    const handledAtValue = p?.last_reviewed_at || p?.reviewed_at || null;
    const lookedUp = lookupReviewerFromOptions(
      p?.last_reviewed_by,
      providerOptions,
      caregiverOptions
    );
    const reviewerId = p?.last_reviewed_by != null && p?.last_reviewed_by !== ''
      ? Number(p.last_reviewed_by)
      : null;
    const handlerName =
      String(p?.last_reviewed_by_name || p?.reviewed_by_name || p?.reviewer_name || '').trim() ||
      lookedUp.name ||
      (Number.isFinite(reviewerId) && reviewerId > 0 ? `User #${reviewerId}` : '');
    const handlerRole =
      String(p?.last_reviewed_by_role || p?.reviewed_by_role || p?.reviewer_role || '').trim() ||
      lookedUp.role ||
      '';

    // A newer unreviewed abnormal reading reopens the episode, even if an older one was reviewed.
    if (p?.abnormal_episode_pending === true) {
      return { reviewed: false, at: null, by: '', role: '', reviewerId: null };
    }

    // Trust closed episode flags from the list API (upload-based reopen).
    if (p?.abnormal_episode_pending === false && handledAtValue) {
      return { reviewed: true, at: handledAtValue, by: handlerName, role: handlerRole, reviewerId };
    }
    if (p?.is_reviewed === true && handledAtValue && !isNewerAbnormalThan(p, handledAtValue)) {
      return { reviewed: true, at: handledAtValue, by: handlerName, role: handlerRole, reviewerId };
    }

    // Timestamp compare (prefer latest_abnormal_uploaded_at over measure clock)
    if (handledAtValue && !isNewerAbnormalThan(p, handledAtValue)) {
      return { reviewed: true, at: handledAtValue, by: handlerName, role: handlerRole, reviewerId };
    }

    return { reviewed: false, at: null, by: '', role: '', reviewerId: null };
  };

  useEffect(() => {
    if (!isAbnormalContext) {
      setIsReviewed(false);
      setPanelMode(null);
      return;
    }

    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // New unreviewed abnormal batch (after a previous review) should reopen Mark Reviewed.
    if (patient?.abnormal_episode_pending === true) {
      patientKeyParts(patient).forEach((part) => {
        reviewedLockByKey.delete(`${pIdResolved || ''}:${part}`);
      });
    }

    // Module lock survives remount (toast success but UI snap-back)
    const lock = findReviewedLock(pIdResolved, patient);
    if (lock && patient?.abnormal_episode_pending !== true && !isNewerAbnormalThan(patient, lock.at)) {
      setIsReviewed(true);
      setReviewedAt(lock.at);
      setReviewedByName(lock.by || '');
      setReviewedByRole(lock.role || '');
      setReviewedById(lock.reviewerId != null ? String(lock.reviewerId) : '');
      setLastInviteRole(null);
      setLastInviteName('');
      return;
    }
    if (lock && isNewerAbnormalThan(patient, lock.at)) {
      patientKeyParts(patient).forEach((part) => {
        reviewedLockByKey.delete(`${pIdResolved || ''}:${part}`);
      });
    }

    const resolved = resolveReviewedFromPatient(patient);
    if (resolved.reviewed) {
      setIsReviewed(true);
      setReviewedAt(resolved.at);
      setReviewedByName(resolved.by);
      setReviewedByRole(resolved.role || '');
      setReviewedById(resolved.reviewerId != null ? String(resolved.reviewerId) : '');
      setLastInviteRole(null);
      setLastInviteName('');
      return;
    }

    setIsReviewed(false);
    setReviewedAt(null);
    setReviewedByName('');
    setReviewedByRole('');
    setReviewedById('');
    setPanelMode(null);
    setSelectedId('');

    // If an assignment exists and is not outdated by a newer reading or review, set invite labels
    const assignedAt = patient?.abnormal_assigned_at;
    const isOutdated = Boolean(
      (assignedAt && isNewerAbnormalThan(patient, assignedAt)) ||
      (assignedAt && patient?.last_reviewed_at && new Date(patient.last_reviewed_at).getTime() >= new Date(assignedAt).getTime() - 1000)
    );

    if (patient?.abnormal_assignee_role && !isOutdated) {
      setLastInviteRole(patient.abnormal_assignee_role);
      setLastInviteName(patient.abnormal_assignee_name || '');
    } else {
      setLastInviteRole(null);
      setLastInviteName('');
    }
  }, [patient, isAbnormalContext, pIdResolved, providerOptions, caregiverOptions]);

  useLayoutEffect(() => {
    if (panelMode !== 'invite' || !compact || !anchorRef.current) {
      setSheetPos(null);
      return undefined;
    }
    const place = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 280;
      const height = 230;
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const openAbove = window.innerHeight - rect.bottom < height && rect.top > height;
      const top = openAbove ? Math.max(8, rect.top - height - 8) : rect.bottom + 8;
      setSheetPos({ top, left, width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [panelMode, compact]);

  const activeOptions = useMemo(() => {
    const list = pickRole === 'provider' ? providerOptions : caregiverOptions;
    const source = Array.isArray(list) ? list : [];
    const roleId = Number(currentUser?.role_id);
    const selfMatchesRole =
      (pickRole === 'provider' && roleId === 4) ||
      (pickRole === 'caregiver' && (roleId === 5 || roleId === 7));
    const others = source.filter((opt) => !optionMatchesCurrentUser(opt, currentUserId));
    if (!selfMatchesRole || !currentUserId) return others;
    const selfOpt = source.find((opt) => optionMatchesCurrentUser(opt, currentUserId)) || {
      id: currentUser.id || currentUser.user_id,
      user_id: currentUser.id || currentUser.user_id,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
      name: currentUserDisplayName(currentUser)
    };
    return [selfOpt, ...others];
  }, [pickRole, providerOptions, caregiverOptions, currentUserId, currentUser]);

  const handleMarkReviewed = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isReviewed || isSavingReview || !pid || !pIdResolved) return;

    setIsSavingReview(true);
    try {
      const response = await api.put(`/practices/${pIdResolved}/patients/${pid}/review`);
      if (response.data?.success) {
        const nextAt =
          response.data.last_reviewed_at || response.data.reviewed_at || new Date().toISOString();
        const nextName =
          response.data.last_reviewed_by_name || currentUserDisplayName(currentUser) || 'You';
        const nextRole =
          response.data.last_reviewed_by_role || currentUserRoleLabel(currentUser);

        const nextReviewerId =
          response.data.last_reviewed_by || currentUserId || null;

        const lock = { at: nextAt, by: nextName, role: nextRole, reviewerId: nextReviewerId, pid: String(pid) };
        setReviewedLock(pIdResolved, patient, lock);
        skipNextSyncRef.current = true;

        setIsReviewed(true);
        setReviewedAt(nextAt);
        setReviewedByName(nextName);
        setReviewedByRole(nextRole);
        setReviewedById(nextReviewerId != null ? String(nextReviewerId) : String(currentUserId || ''));
        setPanelMode(null);
        toast.success(
          Number(response.data.stamped_readings) > 0
            ? `${response.data.stamped_readings} reading${Number(response.data.stamped_readings) === 1 ? '' : 's'} marked as reviewed`
            : 'Marked as reviewed'
        );

        if (onStatusChange) {
          onStatusChange(pid, {
            is_reviewed: true,
            abnormal_episode_pending: false,
            last_reviewed_at: nextAt,
            last_reviewed_by: nextReviewerId,
            last_reviewed_by_name: nextName,
            last_reviewed_by_role: nextRole,
            last_reviewed_by_role_id: response.data.last_reviewed_by_role_id || currentUser?.role_id || null,
            // Prevent stale "newer abnormal" compare from reopening the episode in UI
            latest_abnormal_at: patient?.latest_abnormal_at || nextAt,
            abnormal_measurement_date: patient?.abnormal_measurement_date || nextAt
          });
        }
      }
    } catch (err) {
      console.error('Error marking patient reviewed:', err);
      toast.error(err?.response?.data?.message || 'Failed to mark as reviewed');
    } finally {
      setIsSavingReview(false);
    }
  };

  const handleSendInviteOrNotify = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSavingPick || !pid || !pIdResolved || panelMode !== 'invite') return;
    if (!selectedId) {
      toast.error(`Select a ${pickRole}`);
      return;
    }
    if (selectedReadings24h.length === 0) {
      toast.error('Select at least one reading from the last 7 days');
      setIsModal24hOpen(true);
      return;
    }

    const purpose = 'invite';
    setIsSavingPick(true);
    try {
      const endpoint =
        pickRole === 'provider'
          ? `/practices/${pIdResolved}/patients/${pid}/assign-provider`
          : `/practices/${pIdResolved}/patients/${pid}/assign-caregiver`;

      const payload = {
        ...(pickRole === 'provider' ? { providerId: selectedId } : { caregiverId: selectedId }),
        purpose,
        scope: 'selected',
        readings: selectedReadings24h.map((r) => ({
          vitalType: r.vitalType || r.vital_type,
          measurementId: r.measurementId || r.id
        }))
      };

      const response = await api.put(endpoint, payload);
      if (response.data?.success) {
        const nextName =
          response.data.assignee_name ||
          optionLabel(
            activeOptions.find((opt) => optionId(opt, pickRole) === String(selectedId)),
            pickRole
          ) ||
          `${pickRole} #${selectedId}`;
        const mailed = Boolean(response.data.email_sent);

        setLastInviteRole(pickRole);
        setLastInviteName(nextName);
        skipNextSyncRef.current = true;
        const assignedAtNow = new Date().toISOString();
        const scopeLabel = `${selectedReadings24h.length} reading${selectedReadings24h.length === 1 ? '' : 's'}`;
        toast.success(
          mailed
            ? `${pickRole === 'provider' ? 'Provider' : 'Caregiver'} invited to review ${scopeLabel} — email sent`
            : `${pickRole === 'provider' ? 'Provider' : 'Caregiver'} invited to review ${scopeLabel}`
        );
        setPanelMode(null);
        setSelectedId('');
        setSelectedReadings24h([]);
        if (onStatusChange) {
          onStatusChange(pid, {
            abnormal_assignee_role: pickRole,
            abnormal_assignee_id: selectedId,
            abnormal_assignee_name: nextName,
            abnormal_assigned_to_user_id: selectedId,
            abnormal_assigned_by_user_id: currentUserId,
            abnormal_assigned_by_name: currentUserDisplayName(currentUser),
            abnormal_assigned_by_role: currentUserRoleLabel(currentUser),
            abnormal_assigned_at: response.data.assigned_at || assignedAtNow,
            abnormal_assignment_purpose: purpose
          });
        }
      }
    } catch (err) {
      console.error('Error inviting/notifying:', err);
      toast.error(err?.response?.data?.message || 'Failed to send');
    } finally {
      setIsSavingPick(false);
    }
  };

  const handleUnassign = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUnassigning || isSavingReview || isSavingPick || !pid || !pIdResolved) return;

    setIsUnassigning(true);
    try {
      const response = await patientService.unassignPatientAbnormal(pIdResolved, pid);
      if (response.data?.success) {
        toast.success('Assignment removed. Patient is now open for review.');
        setLastInviteRole(null);
        setLastInviteName('');
        setSelectedId('');
        setPanelMode(null);

        if (onStatusChange) {
          onStatusChange(pid, {
            abnormal_assignee_role: null,
            abnormal_assignee_id: null,
            abnormal_assignee_name: null,
            abnormal_assigned_by_name: null,
            abnormal_assigned_by_role: null,
            abnormal_assigned_by_user_id: null,
            abnormal_assigned_to_user_id: null,
            abnormal_assigned_at: null
          });
        }
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
      toast.error(err?.response?.data?.message || 'Failed to remove assignment');
    } finally {
      setIsUnassigning(false);
    }
  };

  const reviewedAtLabel = formatHandledAt(reviewedAt);
  const busy = isSavingReview || isSavingPick;
  const reviewerIsYou = Boolean(
    (currentUserId && reviewedById && String(reviewedById) === String(currentUserId)) ||
    (!reviewedById &&
      currentUserDisplayName(currentUser) &&
      reviewedByName &&
      currentUserDisplayName(currentUser).toLowerCase() === String(reviewedByName).trim().toLowerCase())
  );

  const renderPicker = (mode) => (
    <div className="prc-assign-sheet">
      <p className="prc-assign-title">Invite to review</p>

      <div className="prc-role-toggle" role="tablist">
        <button
          type="button"
          className={`prc-role-btn${pickRole === 'caregiver' ? ' is-active' : ''}`}
          onClick={() => {
            setPickRole('caregiver');
            setSelectedId('');
          }}
          disabled={busy}
        >
          Caregiver
        </button>
        <button
          type="button"
          className={`prc-role-btn${pickRole === 'provider' ? ' is-active' : ''}`}
          onClick={() => {
            setPickRole('provider');
            setSelectedId('');
          }}
          disabled={busy}
        >
          Provider
        </button>
      </div>

      <select
        className="prc-select"
        value={selectedId}
        onChange={(e) => {
          const nextId = e.target.value;
          setSelectedId(nextId);
          setSelectedReadings24h([]);
          if (nextId) setIsModal24hOpen(true);
        }}
        disabled={busy || activeOptions.length === 0}
      >
        <option value="">
          {activeOptions.length === 0 ? `No ${pickRole}s available` : `Select ${pickRole}…`}
        </option>
        {activeOptions.map((opt) => {
          const id = optionId(opt, pickRole);
          return (
            <option key={id} value={id}>
              {optionMatchesCurrentUser(opt, currentUserId)
                ? `${optionLabel(opt, pickRole)} (Me)`
                : optionLabel(opt, pickRole)}
            </option>
          );
        })}
      </select>

      {selectedId && (
        <div className="prc-reading-wrap">
          {selectedReadings24h.length > 0 ? (
            <div className="prc-24h-selected-summary">
              <span className="prc-24h-count-badge">
                {selectedReadings24h.length} reading{selectedReadings24h.length === 1 ? '' : 's'} selected
              </span>
              <button
                type="button"
                className="prc-24h-edit-btn"
                onClick={() => setIsModal24hOpen(true)}
                disabled={busy}
              >
                Change
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="prc-24h-open-btn"
              onClick={() => setIsModal24hOpen(true)}
              disabled={busy}
            >
              Choose last 7 days readings
            </button>
          )}
        </div>
      )}

      <div className="prc-assign-actions">
        <button
          type="button"
          className="prc-assign-cancel"
          onClick={() => {
            setPanelMode(null);
            setSelectedId('');
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="prc-assign-confirm"
          onClick={handleSendInviteOrNotify}
          disabled={busy || !selectedId || selectedReadings24h.length === 0}
        >
          {isSavingPick ? 'Sending…' : 'Assign & email'}
        </button>
      </div>
    </div>
  );

  if (!isAbnormalContext) {
    return (
      <div className={`patient-review-cell ${compact ? 'prc-compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className="prc-no-flags">No flags</span>
      </div>
    );
  }

  const isSuperAdmin = Number(currentUser?.role_id) === 1;
  const assignOnly = Number(currentUser?.role_id) === 3;
  if (isSuperAdmin || (assignOnly && isReviewed)) {
    return (
      <div className={`patient-review-cell ${compact ? 'prc-compact' : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className={`prc-status-label ${isReviewed ? 'is-reviewed' : 'is-pending'}`}>
          {isReviewed ? 'Reviewed' : 'Pending'}
        </span>
      </div>
    );
  }

  if (isReviewed) {
    const reviewedTitle = [
      reviewerIsYou ? 'Reviewed by you' : (reviewedByName ? `Reviewed by ${reviewedByName}` : 'Reviewed'),
      !reviewerIsYou ? reviewedByRole : '',
      reviewedAtLabel || '',
    ].filter(Boolean).join(' · ');

    return (
      <div className={`patient-review-cell ${compact ? 'prc-compact' : ''} prc-done`} onClick={(e) => e.stopPropagation()}>
        <div className="prc-icon-row">
          <span className="res-action-btn prc-reviewed-icon" title={reviewedTitle}>
            <FontAwesomeIcon icon={faCheck} />
          </span>
        </div>
      </div>
    );
  }

  const assignedAt = patient?.abnormal_assigned_at;
  const isAssignmentOutdated = Boolean(
    isReviewed ||
    (assignedAt && isNewerAbnormalThan(patient, assignedAt)) ||
    (assignedAt && patient?.last_reviewed_at && new Date(patient.last_reviewed_at).getTime() >= new Date(assignedAt).getTime() - 1000)
  );

  const assignedId = isAssignmentOutdated ? null : (patient?.abnormal_assignee_id || patient?.abnormal_assigned_to_user_id || null);
  const assignedById = isAssignmentOutdated ? null : (patient?.abnormal_assigned_by_user_id || patient?.abnormal_assigned_by_id || null);
  const inviteNameDisplay = isAssignmentOutdated ? '' : (lastInviteName || patient?.abnormal_assignee_name || patient?.invited_review_name || '');
  const inviteRoleDisplay = isAssignmentOutdated ? 'Staff' : (lastInviteRole || patient?.abnormal_assignee_role_display || patient?.abnormal_assignee_role || 'Staff');
  const assignedByName = isAssignmentOutdated ? '' : (patient?.abnormal_assigned_by_name || '');
  const assignedByRole = isAssignmentOutdated ? 'Staff' : (patient?.abnormal_assigned_by_role || 'Staff');

  const isAssigned = Boolean(!isReviewed && (assignedId || inviteNameDisplay));
  const isAssignedToMe = Boolean(
    isAssigned && currentUserId && assignedId && String(assignedId) === String(currentUserId)
  );
  const isAssignedToOther = Boolean(
    isAssigned && (!isAssignedToMe)
  );
  const isAssignedByMe = Boolean(
    isAssigned && currentUserId && assignedById && String(assignedById) === String(currentUserId)
  );
  const canReassignOrUnassign = isAssigned && (isAssignedByMe || isAssignedToMe || assignOnly);

  const reviewButtonDisabled = busy || isUnassigning || panelMode === 'invite' || isAssignedToOther;
  let reviewButtonTitle = 'Review this abnormal reading yourself';
  if (isAssignedToOther) {
    const assigneeLabel = inviteNameDisplay || 'staff';
    reviewButtonTitle = `Assigned to ${assigneeLabel}${inviteRoleDisplay ? ` (${inviteRoleDisplay})` : ''}${assignedByName ? ` by ${assignedByRole} ${assignedByName}` : ''}`;
  } else if (isAssignedToMe) {
    reviewButtonTitle = assignedByName
      ? `Assigned to you by ${assignedByRole} ${assignedByName}`
      : 'Assigned to you';
  }

  return (
    <div ref={anchorRef} className={`patient-review-cell ${compact ? 'prc-compact' : ''} prc-pending`} onClick={(e) => e.stopPropagation()}>
      {(panelMode !== 'invite' || compact) && (
        <div className="prc-icon-row" title={isAssigned ? reviewButtonTitle : undefined}>
          {isAssigned ? (
            <>
              {isAssignedToMe && !assignOnly ? (
                <button
                  type="button"
                  className="res-action-btn res-view-btn"
                  onClick={handleMarkReviewed}
                  disabled={reviewButtonDisabled}
                  title={reviewButtonTitle}
                >
                  {isSavingReview ? '…' : <FontAwesomeIcon icon={faCheck} />}
                </button>
              ) : (
                <span
                  className="res-action-btn res-view-btn"
                  title={reviewButtonTitle}
                  style={{ cursor: 'default' }}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                </span>
              )}
              {canReassignOrUnassign && (
                <button
                  type="button"
                  className="res-action-btn res-delete-btn"
                  onClick={handleUnassign}
                  disabled={busy || isUnassigning}
                  title="Remove assignment"
                >
                  {isUnassigning ? '…' : <FontAwesomeIcon icon={faTimes} />}
                </button>
              )}
            </>
          ) : (
            <>
              {!assignOnly && (
                <button
                  type="button"
                  className="res-action-btn res-view-btn"
                  onClick={handleMarkReviewed}
                  disabled={reviewButtonDisabled}
                  title={reviewButtonTitle}
                >
                  {isSavingReview ? '…' : <FontAwesomeIcon icon={faCheck} />}
                </button>
              )}
              <button
                type="button"
                className="res-action-btn res-view-btn"
                onClick={() => {
                  setPanelMode('invite');
                  setPickRole('provider');
                  setSelectedId('');
                  setSelectedReadings24h([]);
                }}
                disabled={busy || isUnassigning}
                title="Assign to review (provider or caregiver)"
              >
                <FontAwesomeIcon icon={faUserPlus} />
              </button>
            </>
          )}
        </div>
      )}

      {panelMode === 'invite' && !compact && renderPicker('invite')}
      {panelMode === 'invite' && compact && sheetPos && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="prc-assign-sheet prc-assign-sheet-fixed"
            style={{ top: sheetPos.top, left: sheetPos.left, width: sheetPos.width }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderPicker('invite').props.children}
          </div>,
          document.body
        )
        : null}

      <AbnormalReadings24hModal
        isOpen={isModal24hOpen}
        onClose={() => setIsModal24hOpen(false)}
        patient={patient}
        practiceId={pIdResolved}
        currentUserId={currentUserId}
        initialSelectedKeys={selectedReadings24h.map(
          (r) => r.key || `${r.vitalType || r.vital_type}_${r.measurementId || r.id}`
        )}
        onConfirm={(chosen) => {
          setSelectedReadings24h(chosen);
        }}
      />
    </div>
  );
};

export default PatientReviewCell;
